// ─── Onboarding Wizard (V3) ─────────────────────────────────────
// 6-step onboarding: Entry -> Describe -> UseCase -> Plan -> Credentials -> Repo -> Launch
// Self-contained component with inline styles and CSS variable theming.

import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import {
  Zap, Check, CheckCircle, Globe, Lock, Mail, CreditCard, Database,
  Shield, Code2, GitBranch, Rocket, ArrowRight, Upload, FileText,
  Plus, X, AlertTriangle, Loader2, Package, Server, Settings,
  ChevronRight, ArrowLeft, Lightbulb, Layers, Search,
} from 'lucide-react';
import api, { getSubscriberRepos, getGithubInstallUrl, type GitHubRepo } from '../api';
import { setPendingEntrySpecFile } from '../features/uploads/pendingEntrySpecFile';
import { setPendingEntryRepo } from '../features/uploads/pendingEntryRepo';
import { RecoveryCard } from './SubscriberChat';
import { CreateChatProjectCard } from './project-detail/components/CreateChatProjectCard';

// ocn-S816-P0: spec files the entry card's file picker accepts. .md/.txt/.html
// are read client-side; .pdf/.docx route through the existing S569
// wizard/parse-spec path (see SubscriberChat.tsx's handleFileAttach).
const ENTRY_SPEC_ACCEPT = '.md,.txt,.html,.pdf,.docx';

// ─── Types ──────────────────────────────────────────────────────

interface ModuleDef {
  id: string;
  name: string;
  required: boolean;
  needs_credentials: boolean;
  credential_fields?: { key: string; label: string; placeholder?: string }[];
}

interface IntegrationDef {
  id: string;
  name: string;
  icon?: string;
}

interface PlanResponse {
  title: string;
  type: string;
  stack: string;
  modules: ModuleDef[];
  integrations: IntegrationDef[];
  estimated_minutes: number;
  estimated_credits: number;
}

type EntryChoice = 'idea' | 'spec' | null;
type UseCaseChoice = 'personal' | 'commercial' | 'platform' | null;

interface Props {
  go: (page: string) => void;
}

// ─── Shared Styles ──────────────────────────────────────────────

const containerStyle: React.CSSProperties = {
  maxWidth: 720,
  margin: '0 auto',
  padding: '40px 24px',
  fontFamily: 'var(--font-sans)',
  color: 'var(--text)',
};

const cardStyle: React.CSSProperties = {
  background: 'var(--bg-secondary)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-lg)',
  padding: '24px',
  marginBottom: 16,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 16px',
  fontSize: 'var(--text-base)',
  fontFamily: 'var(--font-sans)',
  background: 'var(--bg)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-md)',
  color: 'var(--text)',
  outline: 'none',
  boxSizing: 'border-box' as const,
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  minHeight: 160,
  resize: 'vertical' as const,
  fontFamily: 'var(--font-sans)',
  lineHeight: 1.6,
};

const btnPrimary: React.CSSProperties = {
  padding: '12px 24px',
  fontSize: 'var(--text-sm)',
  fontWeight: 600,
  background: 'var(--blue)',
  border: 'none',
  borderRadius: 'var(--radius-md)',
  color: 'white',
  cursor: 'pointer',
  fontFamily: 'var(--font-sans)',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
};

const btnSecondary: React.CSSProperties = {
  ...btnPrimary,
  background: 'transparent',
  border: '1px solid var(--border)',
  color: 'var(--text)',
};

const btnGhost: React.CSSProperties = {
  padding: '8px 16px',
  fontSize: 'var(--text-sm)',
  fontWeight: 500,
  background: 'transparent',
  border: 'none',
  color: 'var(--text-dim)',
  cursor: 'pointer',
  fontFamily: 'var(--font-sans)',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 'var(--text-sm)',
  fontWeight: 500,
  color: 'var(--text-dim)',
  marginBottom: 8,
};

const headingStyle: React.CSSProperties = {
  fontSize: '24px',
  fontWeight: 700,
  margin: '0 0 8px',
  color: 'var(--text)',
};

const subheadingStyle: React.CSSProperties = {
  fontSize: 'var(--text-base)',
  color: 'var(--text-muted)',
  margin: '0 0 32px',
};

// ─── Step Indicator ─────────────────────────────────────────────

function StepIndicator({ current, total }: { current: number; total: number }) {
  return (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 32 }}>
      {Array.from({ length: total }, (_, i: number) => (
        <div
          key={i}
          style={{
            width: i === current ? 24 : 8,
            height: 8,
            borderRadius: 4,
            background: i === current ? 'var(--blue)' : i < current ? 'var(--green)' : 'var(--border)',
            transition: 'all 0.2s ease',
          }}
        />
      ))}
    </div>
  );
}

// ─── Back Button ────────────────────────────────────────────────

function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <button onClick={onClick} style={btnGhost}>
      <ArrowLeft size={16} /> Back
    </button>
  );
}

// ─── Selectable Card ────────────────────────────────────────────

function SelectCard({
  icon: Icon,
  title,
  subtitle,
  selected,
  onClick,
  examples,
}: {
  icon: React.ElementType;
  title: string;
  subtitle: string;
  selected: boolean;
  onClick: () => void;
  examples?: string[];
}) {
  return (
    <button
      onClick={onClick}
      style={{
        ...cardStyle,
        cursor: 'pointer',
        textAlign: 'left' as const,
        width: '100%',
        borderColor: selected ? 'var(--blue)' : 'var(--border)',
        background: selected ? 'var(--blue-dim)' : 'var(--bg-secondary)',
        transition: 'all 0.15s ease',
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 8,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 'var(--radius-md)',
            background: selected ? 'var(--blue)' : 'var(--bg)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon size={20} style={{ color: selected ? 'white' : 'var(--text-dim)' }} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 'var(--text-base)', color: 'var(--text)' }}>
            {title}
          </div>
          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', marginTop: 2 }}>
            {subtitle}
          </div>
        </div>
        {selected && <CheckCircle size={20} style={{ color: 'var(--blue)', flexShrink: 0 }} />}
      </div>
      {examples && examples.length > 0 && (
        <div style={{ marginTop: 4, paddingLeft: 52 }}>
          {examples.map((ex: string, i: number) => (
            <div key={i} style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginTop: 2 }}>
              {ex}
            </div>
          ))}
        </div>
      )}
    </button>
  );
}

// ─── Modal Overlay ──────────────────────────────────────────────

function Modal({
  open,
  onClose,
  children,
}: {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
}) {
  if (!open) return null;
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
      onClick={onClose}
    >
      <div
        style={{
          ...cardStyle,
          maxWidth: 520,
          width: '90%',
          margin: 0,
          boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
        }}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────

export function OnboardingWizard({ go }: Props) {
  // ocn-S816-P0: the "Build an app / Engineer it" working-style choice moved
  // off this entry screen into an in-chat header toggle (SubscriberChat.tsx,
  // wired to the same useAuth().userTier/setUserTier this file used to own
  // directly) -- a tone preference is changeable mid-conversation, not a gate.
  const entrySpecFileInputRef = useRef<HTMLInputElement>(null);

  const handleEntrySpecFileSelected = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    // Stash the file and navigate immediately -- do NOT block on upload/
    // extraction here (spec §B: "navigate right away"). SubscriberChat's
    // mount effect picks this up and runs the existing handleFileAttach path.
    setPendingEntrySpecFile(file);
    go('subscriber-chat:app_wizard');
  }, [go]);

  // ocn-S818-P0 Part 1: "I Have a Repo" card. A minimal single-click picker,
  // no separate "confirm project name" step, matching the "navigate right
  // away" ethos ocn-S816 established for the spec card. Selection stashes the
  // repo and navigates immediately; SubscriberChat's mount effect does the
  // actual project+chat creation (subscriberChatApi.createEntry) and shows the
  // ingest working state -- this component never calls that API directly.
  //
  // 2026-10-15 (G6): the picker previously called
  // api.listGitHubRepos('lumeniqjs') -- the PLATFORM-token org listing that
  // ocn-S630-P0 correctly made service-only. Every subscriber got 403, and
  // because a failed load left repos === null the effect below re-fired in a
  // tight loop (hundreds of 403s per open). It now reads ONLY the caller
  // tenant's own GitHub App installation (/subscriber/repos, same source as
  // McpAccessStatus) and, when nothing is installed, offers the signed
  // one-click "Connect GitHub" install (/github-app-install-capture/install-url).
  // The subscriber only approves on GitHub; OCNexus does the rest.
  const [repoPickerOpen, setRepoPickerOpen] = useState(false);
  const [repoSearch, setRepoSearch] = useState('');
  const [repos, setRepos] = useState<GitHubRepo[] | null>(null);
  const [reposInstalled, setReposInstalled] = useState<boolean | null>(null);
  const [reposLoading, setReposLoading] = useState(false);
  const [reposError, setReposError] = useState<string | null>(null);
  const [reposAttempted, setReposAttempted] = useState(false);
  const [connectingGithub, setConnectingGithub] = useState(false);

  const loadRepos = useCallback(() => {
    setReposAttempted(true);
    setReposLoading(true);
    setReposError(null);
    getSubscriberRepos()
      .then((res) => {
        const names = Array.isArray(res?.repos) ? res.repos : [];
        setReposInstalled(Boolean(res?.installed));
        setRepos(names.map((fullName: string) => ({
          full_name: fullName,
          name: fullName.includes('/') ? fullName.slice(fullName.indexOf('/') + 1) : fullName,
          description: null,
          language: null,
        }) as GitHubRepo));
      })
      .catch((err: unknown) => setReposError(err instanceof Error ? err.message : 'Failed to load repositories'))
      .finally(() => setReposLoading(false));
  }, []);

  // Load once per open. A failure shows the RecoveryCard (explicit Retry)
  // instead of re-firing automatically.
  useEffect(() => {
    if (repoPickerOpen && !reposAttempted && !reposLoading) loadRepos();
  }, [repoPickerOpen, reposAttempted, reposLoading, loadRepos]);

  useEffect(() => {
    if (!repoPickerOpen) setReposAttempted(false);
  }, [repoPickerOpen]);

  const handleConnectGithub = useCallback(() => {
    setConnectingGithub(true);
    setReposError(null);
    getGithubInstallUrl()
      .then(({ url }) => { window.location.assign(url); })
      .catch((err: unknown) => {
        setConnectingGithub(false);
        setReposError(err instanceof Error ? err.message : 'GitHub connection could not start');
      });
  }, []);

  const filteredRepos = useMemo(() => {
    if (!repos) return [];
    const q = repoSearch.toLowerCase().trim();
    if (!q) return repos;
    return repos.filter((r: GitHubRepo) => r.name.toLowerCase().includes(q) || (r.description || '').toLowerCase().includes(q));
  }, [repos, repoSearch]);

  const handleRepoSelected = useCallback((repo: GitHubRepo) => {
    setPendingEntryRepo({ fullName: repo.full_name, name: repo.name, description: repo.description, language: repo.language });
    setRepoPickerOpen(false);
    go('subscriber-chat:app_wizard');
  }, [go]);

  // -- Navigation --
  const [step, setStep] = useState(-1); // -1 = entry screen
  const totalSteps = 6; // 0-5

  // ── Entry ──
  // ocn-S816-P0: both entry cards now navigate immediately (no more
  // setEntryChoice + "Get Started" gate) -- entryChoice itself is read by the
  // now-unreachable step 0-5 flow below, kept as-is (out of this fire's
  // declared scope) so that dead path still compiles.
  const [entryChoice] = useState<EntryChoice>(null);

  // ── Step 1: Describe / Upload ──
  const [projectName, setProjectName] = useState('');
  const [description, setDescription] = useState('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [pastedSpec, setPastedSpec] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  // ── Step 2: Use case ──
  const [useCase, setUseCase] = useState<UseCaseChoice>(null);
  const [showMultiTenantModal, setShowMultiTenantModal] = useState(false);

  // ── Step 3: Plan ──
  const [plan, setPlan] = useState<PlanResponse | null>(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [planError, setPlanError] = useState<string | null>(null);
  const [adjusting, setAdjusting] = useState(false);
  const [selectedModules, setSelectedModules] = useState<Set<string>>(new Set());
  // Populated by wizardV3.generatePlan (composed start+plan|analyze). Required
  // at launch — backend /wizard/v3/launch refuses requests without it.
  const [projectId, setProjectId] = useState<string | null>(null);

  // ── Step 4: Credentials ──
  const [credentials, setCredentials] = useState<Record<string, Record<string, string>>>({});
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<Record<string, boolean> | null>(null);

  // ── Step 5: Repo ──
  const [repoMode, setRepoMode] = useState<'create' | 'connect'>('create');
  const [repoName, setRepoName] = useState('');
  const [repoPrivate, setRepoPrivate] = useState(true);
  const [existingRepoUrl, setExistingRepoUrl] = useState('');

  // ── Step 6: Launch ──
  const [launching, setLaunching] = useState(false);
  const [launchError, setLaunchError] = useState<string | null>(null);

  // ── Helpers ──

  const needsCredentials = useCallback((): boolean => {
    if (!plan) return false;
    return plan.modules.some(
      (m: ModuleDef) => selectedModules.has(m.id) && m.needs_credentials
    );
  }, [plan, selectedModules]);

  const getCredentialModules = useCallback((): ModuleDef[] => {
    if (!plan) return [];
    return plan.modules.filter(
      (m: ModuleDef) => selectedModules.has(m.id) && m.needs_credentials
    );
  }, [plan, selectedModules]);

  // Compute actual step index accounting for conditional credentials step
  const getEffectiveSteps = useCallback((): number[] => {
    // steps: 0=describe, 1=usecase, 2=plan, 3=credentials(conditional), 4=repo, 5=launch
    if (needsCredentials()) return [0, 1, 2, 3, 4, 5];
    return [0, 1, 2, 4, 5]; // skip credentials
  }, [needsCredentials]);

  const goNext = useCallback(() => {
    if (step === 2 && !needsCredentials()) {
      setStep(4); // skip credentials
    } else {
      setStep((s: number) => s + 1);
    }
  }, [step, needsCredentials]);

  const goBack = useCallback(() => {
    if (step === 4 && !needsCredentials()) {
      setStep(2); // skip credentials going back
    } else if (step === 0) {
      setStep(-1); // back to entry
    } else {
      setStep((s: number) => s - 1);
    }
  }, [step, needsCredentials]);

  // ── API Calls ──

  const fetchPlan = useCallback(async () => {
    setPlanLoading(true);
    setPlanError(null);
    try {
      const payload: Record<string, unknown> = {
        name: projectName,
        use_case: useCase,
      };
      if (entryChoice === 'idea') {
        payload.description = description;
      } else {
        payload.spec_text = pastedSpec;
        // File upload would be handled separately
      }

      const result = await (api as any).wizardV3.generatePlan(payload);
      setProjectId(result.project_id);
      setPlan(result);
      const mods = new Set<string>();
      result.modules.forEach((m: ModuleDef) => {
        mods.add(m.id);
      });
      setSelectedModules(mods);
    } catch (err: unknown) {
      setPlanError(err instanceof Error ? err.message : 'Failed to generate plan');
    } finally {
      setPlanLoading(false);
    }
  }, [projectName, useCase, entryChoice, description, pastedSpec]);

  const validateCredentials = useCallback(async () => {
    setValidating(true);
    try {
      const result = await (api as any).wizardV3.validateCredentials(credentials);
      setValidationResult(result);
    } catch {
      // Validation failed silently, user can retry
    } finally {
      setValidating(false);
    }
  }, [credentials]);

  const launchProject = useCallback(async () => {
    setLaunching(true);
    setLaunchError(null);
    try {
      if (!projectId) {
        throw new Error('Project not initialized — generate a plan first');
      }
      const derivedRepoName = repoName || projectName.toLowerCase().replace(/\s+/g, '-');
      const payload: {
        project_id: string;
        modules: string[];
        repo_name?: string;
        repo_visibility?: 'public' | 'private';
        create_repo?: boolean;
        credentials: Record<string, Record<string, string>>;
      } = {
        project_id: projectId,
        modules: Array.from(selectedModules),
        credentials,
      };
      if (repoMode === 'create') {
        payload.create_repo = true;
        payload.repo_name = derivedRepoName;
        payload.repo_visibility = repoPrivate ? 'private' : 'public';
      } else {
        // Connect-existing: backend currently has no repo URL field on launch,
        // so we just skip create_repo and rely on the operator to wire the
        // existing repo via the addproject flow. existingRepoUrl is intentionally
        // unused in the payload until the backend grows a `connect_repo_url`.
        payload.create_repo = false;
      }

      const result = await (api as any).wizardV3.launch(payload);
      go('project:' + result.project_id);
    } catch (err: unknown) {
      setLaunchError(err instanceof Error ? err.message : 'Launch failed');
    } finally {
      setLaunching(false);
    }
  }, [projectId, projectName, selectedModules, credentials, repoMode, repoName, repoPrivate, go]);

  // ── File handling ──

  const handleFileDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      const ext = file.name.split('.').pop()?.toLowerCase();
      if (['pdf', 'md', 'docx', 'txt'].includes(ext || '')) {
        setUploadedFile(file);
      }
    }
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setUploadedFile(file);
  }, []);

  // ── Trigger plan fetch when entering step 3 ──
  const handleUseCaseNext = useCallback(() => {
    setStep(2);
    fetchPlan();
  }, [fetchPlan]);

  // ── Step Rendering ──

  const effectiveSteps = getEffectiveSteps();
  const effectiveIndex = effectiveSteps.indexOf(step);
  const effectiveTotal = effectiveSteps.length;

  // ─── Entry Screen ─────────────────────────────────────────────

  if (step === -1) {
    return (
      <>
      <div style={containerStyle}>
        {/* ocn-S818-P0: three equal-weight entry cards (New Idea / I Have a
            Spec / I Have a Repo), side by side, stacking below the mobile
            breakpoint. No mode cards, no second decision layer -- the choice
            made here changes what happens NEXT. */}
        <style>{`
          @media (max-width: 640px) {
            .ocn-entry-cards-grid { grid-template-columns: 1fr !important; }
          }
        `}</style>
        <input
          ref={entrySpecFileInputRef}
          type="file"
          accept={ENTRY_SPEC_ACCEPT}
          style={{ display: 'none' }}
          onChange={handleEntrySpecFileSelected}
        />
        <div style={{ textAlign: 'center' as const, marginBottom: 48 }}>
          <Zap size={36} style={{ color: 'var(--blue)', marginBottom: 16 }} />
          <h1 style={{ ...headingStyle, fontSize: '28px' }}>What are we building today?</h1>
          <p style={subheadingStyle}>Choose how you want to get started</p>
        </div>

        <div
          className="ocn-entry-cards-grid"
          style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, maxWidth: 900, margin: '0 auto' }}
        >
          <SelectCard
            icon={Lightbulb}
            title="New Idea"
            subtitle="Describe your app and I'll build it"
            selected={false}
            // New Idea -> straight into chat, composer focused (reuses the
            // existing "Build a new app" entry route unchanged -- ocn-S278-P0).
            onClick={() => go('subscriber-chat:app_wizard')}
          />
          <SelectCard
            icon={FileText}
            title="I Have a Spec"
            subtitle="Upload a PRD, brief, or project doc"
            selected={false}
            // I Have a Spec -> open the native file picker IMMEDIATELY, right
            // from this card. On selection, handleEntrySpecFileSelected
            // navigates to chat without waiting for extraction.
            onClick={() => entrySpecFileInputRef.current?.click()}
          />
          <SelectCard
            icon={GitBranch}
            title="I Have a Repo"
            subtitle="Connect an existing codebase"
            selected={false}
            // I Have a Repo -> open the repo picker. On selection, stash the
            // repo and navigate immediately (ocn-S818-P0 Part 1) -- SAME
            // "navigate right away" pattern as the spec card above.
            onClick={() => setRepoPickerOpen(true)}
          />
        </div>
        <CreateChatProjectCard go={go} />
      </div>

      <Modal open={repoPickerOpen} onClose={() => setRepoPickerOpen(false)}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>Connect a repository</h3>
          <button onClick={() => setRepoPickerOpen(false)} style={{ ...btnGhost, padding: 4 }}><X size={16} /></button>
        </div>
        <div style={{ position: 'relative', marginBottom: 12 }}>
          <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input
            style={{ ...inputStyle, paddingLeft: 34 }}
            placeholder="Filter repositories..."
            value={repoSearch}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRepoSearch(e.target.value)}
          />
        </div>
        <div style={{ maxHeight: 340, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)' }}>
          {reposLoading && (
            <div style={{ padding: 32, textAlign: 'center' as const }}>
              <Loader2 size={20} style={{ color: 'var(--blue)', animation: 'spin 1s linear infinite' }} />
              <div style={{ marginTop: 8, fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>Loading repositories...</div>
              <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
            </div>
          )}
          {!reposLoading && reposError && (
            // ocn-S818-P0 Part 6: never a bare error string -- REUSE the
            // ocn-S809 RecoveryCard rather than writing a second one.
            <div style={{ padding: 12 }}>
              <RecoveryCard
                reasonCode={null}
                onRetry={loadRepos}
                onBackToDashboard={() => { setRepoPickerOpen(false); go('overview'); }}
                onStartNewChat={() => { setRepoPickerOpen(false); go('subscriber-chat:app_wizard'); }}
              />
            </div>
          )}
          {!reposLoading && !reposError && filteredRepos.length === 0 && (
            <div style={{ padding: 20, textAlign: 'center' as const, fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
              {repoSearch
                ? 'No matching repositories.'
                : reposInstalled
                  ? 'No repositories are shared with OCNexus yet. Add one on GitHub, then reopen this list.'
                  : 'Connect GitHub to choose a repository. You only approve access on GitHub; OCNexus sets up the rest.'}
              {!repoSearch && (
                <div style={{ marginTop: 12 }}>
                  <button
                    onClick={handleConnectGithub}
                    disabled={connectingGithub}
                    style={{ ...btnPrimary, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  >
                    <GitBranch size={14} />
                    {connectingGithub ? 'Opening GitHub...' : reposInstalled ? 'Manage repository access' : 'Connect GitHub'}
                  </button>
                </div>
              )}
            </div>
          )}
          {!reposLoading && !reposError && filteredRepos.map((r: GitHubRepo) => (
            <button
              key={r.full_name}
              onClick={() => handleRepoSelected(r)}
              style={{
                width: '100%', textAlign: 'left' as const, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                borderTop: '1px solid var(--border)', background: 'transparent', border: 'none',
                borderBottom: 'none', borderLeft: 'none', borderRight: 'none', color: 'var(--text)',
                fontFamily: 'var(--font-sans)',
              }}
            >
              <GitBranch size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 'var(--text-sm)', fontWeight: 500 }}>{r.name}</div>
                {r.description && (
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{r.description}</div>
                )}
              </div>
              {r.language && <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>{r.language}</span>}
            </button>
          ))}
        </div>
      </Modal>
      </>
    );
  }

  // ─── Step 0: Describe / Upload ────────────────────────────────

  if (step === 0) {
    return (
      <div style={containerStyle}>
        <BackButton onClick={goBack} />
        <StepIndicator current={effectiveIndex} total={effectiveTotal} />

        <h2 style={headingStyle}>
          {entryChoice === 'idea' ? 'Describe Your Project' : 'Upload Your Spec'}
        </h2>
        <p style={subheadingStyle}>
          {entryChoice === 'idea'
            ? 'Tell us what you want to build. The more detail, the better.'
            : 'Upload your project document or paste the content below.'}
        </p>

        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Project Name</label>
          <input
            style={inputStyle}
            value={projectName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setProjectName(e.target.value)}
            placeholder="My Awesome App"
          />
        </div>

        {entryChoice === 'idea' ? (
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>What are you building?</label>
            <textarea
              style={textareaStyle}
              value={description}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
              placeholder={
                'Example: A SaaS platform for freelancers to manage invoices, track time, and send proposals to clients. ' +
                'It should have user authentication, a dashboard with analytics, Stripe for payments, and email notifications ' +
                'when a proposal is accepted.'
              }
            />
          </div>
        ) : (
          <>
            <div
              style={{
                ...cardStyle,
                border: isDragging ? '2px dashed var(--blue)' : '2px dashed var(--border)',
                background: isDragging ? 'var(--blue-dim)' : 'var(--bg)',
                textAlign: 'center' as const,
                cursor: 'pointer',
                padding: '40px 24px',
                transition: 'all 0.15s ease',
              }}
              onDragOver={(e: React.DragEvent) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleFileDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.md,.docx,.txt"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
              {uploadedFile ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
                  <FileText size={24} style={{ color: 'var(--green)' }} />
                  <span style={{ fontSize: 'var(--text-base)', color: 'var(--text)' }}>
                    {uploadedFile.name}
                  </span>
                  <button
                    onClick={(e: React.MouseEvent) => { e.stopPropagation(); setUploadedFile(null); }}
                    style={{ ...btnGhost, padding: 4 }}
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <>
                  <Upload size={32} style={{ color: 'var(--text-muted)', marginBottom: 12 }} />
                  <div style={{ fontSize: 'var(--text-base)', color: 'var(--text-dim)', marginBottom: 4 }}>
                    Drop your file here or click to browse
                  </div>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                    PDF, Markdown, DOCX, or TXT
                  </div>
                </>
              )}
            </div>

            <div style={{ marginTop: 16, marginBottom: 20 }}>
              <label style={labelStyle}>Or paste your spec</label>
              <textarea
                style={textareaStyle}
                value={pastedSpec}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setPastedSpec(e.target.value)}
                placeholder="Paste your project spec, PRD, or brief here..."
              />
            </div>
          </>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={() => setStep(1)}
            disabled={!projectName.trim() || (entryChoice === 'idea' ? !description.trim() : (!uploadedFile && !pastedSpec.trim()))}
            style={{
              ...btnPrimary,
              opacity: (!projectName.trim() || (entryChoice === 'idea' ? !description.trim() : (!uploadedFile && !pastedSpec.trim()))) ? 0.5 : 1,
            }}
          >
            Continue <ArrowRight size={16} />
          </button>
        </div>
      </div>
    );
  }

  // ─── Step 1: Use Case ─────────────────────────────────────────

  if (step === 1) {
    return (
      <div style={containerStyle}>
        <BackButton onClick={goBack} />
        <StepIndicator current={effectiveIndex} total={effectiveTotal} />

        <h2 style={headingStyle}>How Will This Be Used?</h2>
        <p style={subheadingStyle}>This helps us configure the right architecture and integrations.</p>

        <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 12 }}>
          <SelectCard
            icon={Globe}
            title="Personal"
            subtitle="A project for yourself or a small group"
            selected={useCase === 'personal'}
            onClick={() => { setUseCase('personal'); setShowMultiTenantModal(false); }}
            examples={[
              'Personal portfolio or blog',
              'Internal tool for your team',
              'Side project or prototype',
            ]}
          />
          <SelectCard
            icon={Server}
            title="Commercial Single-Tenant"
            subtitle="A product for one customer or company"
            selected={useCase === 'commercial'}
            onClick={() => { setUseCase('commercial'); setShowMultiTenantModal(false); }}
            examples={[
              'Client project or agency work',
              'Single-company SaaS tool',
              'Custom business application',
            ]}
          />
          <SelectCard
            icon={Layers}
            title="Platform Multi-Tenant"
            subtitle="A SaaS platform serving many organizations"
            selected={useCase === 'platform'}
            onClick={() => { setUseCase('platform'); setShowMultiTenantModal(true); }}
            examples={[
              'B2B SaaS with team workspaces',
              'Marketplace or platform',
              'White-label / multi-org product',
            ]}
          />
        </div>

        <Modal open={showMultiTenantModal} onClose={() => setShowMultiTenantModal(false)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <AlertTriangle size={24} style={{ color: 'var(--orange)' }} />
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>Multi-Tenant Architecture</h3>
          </div>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-dim)', lineHeight: 1.6, margin: '0 0 12px' }}>
            Multi-tenant platforms require additional infrastructure and architectural patterns:
          </p>
          <ul style={{ fontSize: 'var(--text-sm)', color: 'var(--text-dim)', lineHeight: 1.8, paddingLeft: 20, margin: '0 0 24px' }}>
            <li>Tenant isolation and data partitioning</li>
            <li>Per-tenant billing and usage metering</li>
            <li>Role-based access across organizations</li>
            <li>Higher estimated complexity and setup time</li>
          </ul>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <button
              onClick={() => { setUseCase('commercial'); setShowMultiTenantModal(false); }}
              style={btnSecondary}
            >
              Switch to Single-Tenant
            </button>
            <button
              onClick={() => setShowMultiTenantModal(false)}
              style={btnPrimary}
            >
              Continue with Multi-Tenant <ArrowRight size={16} />
            </button>
          </div>
        </Modal>

        {useCase && !showMultiTenantModal && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
            <button onClick={handleUseCaseNext} style={btnPrimary}>
              Generate Plan <Rocket size={16} />
            </button>
          </div>
        )}
      </div>
    );
  }

  // ─── Step 2: Project Plan ─────────────────────────────────────

  if (step === 2) {
    return (
      <div style={containerStyle}>
        <BackButton onClick={goBack} />
        <StepIndicator current={effectiveIndex} total={effectiveTotal} />

        <h2 style={headingStyle}>Your Project Plan</h2>
        <p style={subheadingStyle}>Review what we will build for you.</p>

        {planLoading && (
          <div style={{ ...cardStyle, textAlign: 'center' as const, padding: '60px 24px' }}>
            <Loader2 size={32} style={{ color: 'var(--blue)', animation: 'spin 1s linear infinite', marginBottom: 16 }} />
            <div style={{ fontSize: 'var(--text-base)', color: 'var(--text-dim)' }}>
              Analyzing your project and generating a plan...
            </div>
            <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
          </div>
        )}

        {planError && (
          <div style={{ ...cardStyle, borderColor: 'var(--red)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <AlertTriangle size={20} style={{ color: 'var(--red)' }} />
              <span style={{ color: 'var(--red)', fontWeight: 600 }}>Failed to generate plan</span>
            </div>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-dim)', margin: '0 0 16px' }}>{planError}</p>
            <button onClick={fetchPlan} style={btnSecondary}>
              <Loader2 size={14} /> Retry
            </button>
          </div>
        )}

        {plan && !planLoading && (
          <>
            <div style={cardStyle}>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                <Package size={24} style={{ color: 'var(--blue)' }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: '18px' }}>{plan.title}</div>
                </div>
                <span style={{
                  padding: '4px 12px',
                  fontSize: 'var(--text-xs)',
                  fontWeight: 600,
                  background: useCase === 'platform' ? 'var(--purple)' : useCase === 'commercial' ? 'var(--orange)' : 'var(--green)',
                  color: 'white',
                  borderRadius: 'var(--radius-md)',
                  textTransform: 'uppercase' as const,
                }}>
                  {plan.type}
                </span>
              </div>

              {/* Stack */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, padding: '10px 14px', background: 'var(--bg)', borderRadius: 'var(--radius-md)' }}>
                <Code2 size={16} style={{ color: 'var(--text-muted)' }} />
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-dim)' }}>
                  {plan.stack || 'Next.js 14 + TypeScript + Tailwind + Supabase'}
                </span>
              </div>

              {/* Modules */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ ...labelStyle, marginBottom: 12 }}>Modules</label>
                {plan.modules.map((mod: ModuleDef) => (
                  <div
                    key={mod.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '8px 12px',
                      marginBottom: 4,
                      borderRadius: 'var(--radius-md)',
                      background: selectedModules.has(mod.id) ? 'var(--bg)' : 'transparent',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedModules.has(mod.id)}
                      disabled={mod.required || !adjusting}
                      onChange={() => {
                        setSelectedModules((prev: Set<string>) => {
                          const next = new Set(prev);
                          if (next.has(mod.id)) next.delete(mod.id);
                          else next.add(mod.id);
                          return next;
                        });
                      }}
                      style={{ cursor: mod.required || !adjusting ? 'default' : 'pointer' }}
                    />
                    <span style={{ flex: 1, fontSize: 'var(--text-sm)', color: 'var(--text)' }}>
                      {mod.name}
                    </span>
                    {mod.required && (
                      <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                        required
                      </span>
                    )}
                    {mod.needs_credentials && (
                      <CreditCard size={14} style={{ color: 'var(--orange)' }} />
                    )}
                  </div>
                ))}
              </div>

              {/* Integrations */}
              {plan.integrations && plan.integrations.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>Integrations Detected</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: 8 }}>
                    {plan.integrations.map((integ: IntegrationDef) => (
                      <span
                        key={integ.id}
                        style={{
                          padding: '4px 12px',
                          fontSize: 'var(--text-xs)',
                          background: 'var(--bg)',
                          border: '1px solid var(--border)',
                          borderRadius: 'var(--radius-md)',
                          color: 'var(--text-dim)',
                        }}
                      >
                        {integ.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Estimates */}
              <div style={{
                display: 'flex',
                gap: 16,
                padding: '12px 14px',
                background: 'var(--bg)',
                borderRadius: 'var(--radius-md)',
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginBottom: 2 }}>
                    Estimated Setup
                  </div>
                  <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--text)' }}>
                    ~{plan.estimated_minutes} min
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginBottom: 2 }}>
                    Estimated Credits
                  </div>
                  <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--text)' }}>
                    {plan.estimated_credits} credits
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button
                onClick={() => setAdjusting((a: boolean) => !a)}
                style={btnSecondary}
              >
                <Settings size={16} /> {adjusting ? 'Done Adjusting' : 'Let Me Adjust'}
              </button>
              <button onClick={goNext} style={btnPrimary}>
                Looks Good <Check size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  // ─── Step 3: Credentials ──────────────────────────────────────

  if (step === 3) {
    const credModules = getCredentialModules();
    return (
      <div style={containerStyle}>
        <BackButton onClick={goBack} />
        <StepIndicator current={effectiveIndex} total={effectiveTotal} />

        <h2 style={headingStyle}>Service Credentials</h2>
        <p style={subheadingStyle}>
          Some modules need API keys to work. You can add them now or skip and configure later.
        </p>

        {credModules.map((mod: ModuleDef) => (
          <div key={mod.id} style={{ ...cardStyle, marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <Shield size={20} style={{ color: 'var(--orange)' }} />
              <span style={{ fontWeight: 600, fontSize: 'var(--text-base)' }}>{mod.name}</span>
            </div>

            {(mod.credential_fields || []).map((field: { key: string; label: string; placeholder?: string }) => (
              <div key={field.key} style={{ marginBottom: 12 }}>
                <label style={labelStyle}>{field.label}</label>
                <input
                  type="password"
                  style={inputStyle}
                  placeholder={field.placeholder || field.key}
                  value={credentials[mod.id]?.[field.key] || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setCredentials((prev: Record<string, Record<string, string>>) => ({
                      ...prev,
                      [mod.id]: { ...prev[mod.id], [field.key]: e.target.value },
                    }));
                  }}
                />
              </div>
            ))}

            {validationResult && validationResult[mod.id] !== undefined && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 12px',
                borderRadius: 'var(--radius-md)',
                background: validationResult[mod.id] ? 'var(--green-dim)' : 'var(--orange-dim)',
                marginTop: 8,
              }}>
                {validationResult[mod.id]
                  ? <><CheckCircle size={16} style={{ color: 'var(--green)' }} /><span style={{ fontSize: 'var(--text-sm)', color: 'var(--green)' }}>Validated</span></>
                  : <><AlertTriangle size={16} style={{ color: 'var(--orange)' }} /><span style={{ fontSize: 'var(--text-sm)', color: 'var(--orange)' }}>Could not validate</span></>
                }
              </div>
            )}
          </div>
        ))}

        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <button onClick={goNext} style={{ ...btnGhost, color: 'var(--text-muted)' }}>
            Skip -- I'll add these later <ChevronRight size={14} />
          </button>
          <div style={{ display: 'flex', gap: 12 }}>
            <button
              onClick={validateCredentials}
              disabled={validating}
              style={btnSecondary}
            >
              {validating ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Shield size={14} />}
              {validating ? 'Validating...' : 'Validate Keys'}
            </button>
            <button onClick={goNext} style={btnPrimary}>
              Continue <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Step 4: GitHub Repo ──────────────────────────────────────

  if (step === 4) {
    const defaultRepoName = projectName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    return (
      <div style={containerStyle}>
        <BackButton onClick={goBack} />
        <StepIndicator current={effectiveIndex} total={effectiveTotal} />

        <h2 style={headingStyle}>GitHub Repository</h2>
        <p style={subheadingStyle}>Where should we put the code?</p>

        <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
          <button
            onClick={() => setRepoMode('create')}
            style={{
              ...cardStyle,
              flex: 1,
              cursor: 'pointer',
              textAlign: 'center' as const,
              borderColor: repoMode === 'create' ? 'var(--blue)' : 'var(--border)',
              background: repoMode === 'create' ? 'var(--blue-dim)' : 'var(--bg-secondary)',
            }}
          >
            <Plus size={24} style={{ color: repoMode === 'create' ? 'var(--blue)' : 'var(--text-muted)', marginBottom: 8 }} />
            <div style={{ fontWeight: 600, fontSize: 'var(--text-sm)' }}>Create New Repo</div>
          </button>
          <button
            onClick={() => setRepoMode('connect')}
            style={{
              ...cardStyle,
              flex: 1,
              cursor: 'pointer',
              textAlign: 'center' as const,
              borderColor: repoMode === 'connect' ? 'var(--blue)' : 'var(--border)',
              background: repoMode === 'connect' ? 'var(--blue-dim)' : 'var(--bg-secondary)',
            }}
          >
            <GitBranch size={24} style={{ color: repoMode === 'connect' ? 'var(--blue)' : 'var(--text-muted)', marginBottom: 8 }} />
            <div style={{ fontWeight: 600, fontSize: 'var(--text-sm)' }}>Connect Existing Repo</div>
          </button>
        </div>

        {repoMode === 'create' ? (
          <div style={cardStyle}>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Repository Name</label>
              <input
                style={inputStyle}
                value={repoName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRepoName(e.target.value)}
                placeholder={defaultRepoName}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <label style={{ ...labelStyle, margin: 0 }}>Visibility</label>
              <button
                onClick={() => setRepoPrivate(true)}
                style={{
                  ...btnGhost,
                  padding: '6px 14px',
                  borderRadius: 'var(--radius-md)',
                  background: repoPrivate ? 'var(--bg)' : 'transparent',
                  border: repoPrivate ? '1px solid var(--border)' : '1px solid transparent',
                  color: repoPrivate ? 'var(--text)' : 'var(--text-muted)',
                }}
              >
                <Lock size={14} /> Private
              </button>
              <button
                onClick={() => setRepoPrivate(false)}
                style={{
                  ...btnGhost,
                  padding: '6px 14px',
                  borderRadius: 'var(--radius-md)',
                  background: !repoPrivate ? 'var(--bg)' : 'transparent',
                  border: !repoPrivate ? '1px solid var(--border)' : '1px solid transparent',
                  color: !repoPrivate ? 'var(--text)' : 'var(--text-muted)',
                }}
              >
                <Globe size={14} /> Public
              </button>
            </div>
          </div>
        ) : (
          <div style={cardStyle}>
            <label style={labelStyle}>Repository URL</label>
            <input
              style={inputStyle}
              value={existingRepoUrl}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setExistingRepoUrl(e.target.value)}
              placeholder="https://github.com/your-org/your-repo"
            />
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginTop: 8 }}>
              The repository should be empty or have only an initial commit.
            </p>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <button onClick={goNext} style={btnPrimary}>
            Continue <ArrowRight size={16} />
          </button>
        </div>
      </div>
    );
  }

  // ─── Step 5: Review & Launch ──────────────────────────────────

  if (step === 5) {
    const defaultRepoName = projectName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    const displayRepoName = repoMode === 'create' ? (repoName || defaultRepoName) : existingRepoUrl;
    const activeModules = plan ? plan.modules.filter((m: ModuleDef) => selectedModules.has(m.id)) : [];
    const credModules = getCredentialModules();
    const configuredCreds = credModules.filter((m: ModuleDef) =>
      m.credential_fields?.some((f: { key: string }) => credentials[m.id]?.[f.key])
    );

    return (
      <div style={containerStyle}>
        <BackButton onClick={goBack} />
        <StepIndicator current={effectiveIndex} total={effectiveTotal} />

        <h2 style={headingStyle}>Review & Launch</h2>
        <p style={subheadingStyle}>Everything looks good? Let's ship it.</p>

        <div style={cardStyle}>
          {/* Summary rows */}
          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '12px 16px', fontSize: 'var(--text-sm)' }}>
            <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>Project</span>
            <span style={{ color: 'var(--text)' }}>{projectName}</span>

            <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>Type</span>
            <span style={{ color: 'var(--text)', textTransform: 'capitalize' as const }}>{useCase}</span>

            <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>Stack</span>
            <span style={{ color: 'var(--text)' }}>{plan?.stack || 'Next.js 14 + TypeScript + Tailwind + Supabase'}</span>

            <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>Repository</span>
            <span style={{ color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 6 }}>
              {repoMode === 'create' ? <Lock size={12} /> : <GitBranch size={12} />}
              {displayRepoName}
            </span>

            <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>Modules</span>
            <span style={{ color: 'var(--text)' }}>
              {activeModules.length} selected
              {activeModules.length > 0 && (
                <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>
                  ({activeModules.map((m: ModuleDef) => m.name).join(', ')})
                </span>
              )}
            </span>

            {plan?.integrations && plan.integrations.length > 0 && (
              <>
                <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>Integrations</span>
                <span style={{ color: 'var(--text)' }}>
                  {plan.integrations.map((i: IntegrationDef) => i.name).join(', ')}
                </span>
              </>
            )}

            {credModules.length > 0 && (
              <>
                <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>Credentials</span>
                <span style={{ color: 'var(--text)' }}>
                  {configuredCreds.length}/{credModules.length} configured
                </span>
              </>
            )}

            {plan && (
              <>
                <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>Estimate</span>
                <span style={{ color: 'var(--text)' }}>
                  ~{plan.estimated_minutes} min / {plan.estimated_credits} credits
                </span>
              </>
            )}
          </div>
        </div>

        {launchError && (
          <div style={{
            ...cardStyle,
            borderColor: 'var(--red)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '14px 18px',
          }}>
            <AlertTriangle size={20} style={{ color: 'var(--red)', flexShrink: 0 }} />
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--red)' }}>{launchError}</span>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
          <button
            onClick={launchProject}
            disabled={launching}
            style={{
              ...btnPrimary,
              background: 'var(--green)',
              padding: '14px 32px',
              fontSize: 'var(--text-base)',
              opacity: launching ? 0.7 : 1,
            }}
          >
            {launching ? (
              <>
                <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                Launching...
              </>
            ) : (
              <>
                <Rocket size={18} />
                Launch Project
              </>
            )}
          </button>
        </div>

        {launching && (
          <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
        )}
      </div>
    );
  }

  // Fallback
  return null;
}

export default OnboardingWizard;
